"use client";

import { useChains } from "@opensea/app-kit";
import { omit } from "es-toolkit";
import { useCallback, useEffect, useRef } from "react";
import { useSnapshot } from "valtio";
import { BaseError as ViemBaseError } from "viem";
import { useConnect as useWagmiConnect, useSwitchAccount as useWagmiSwitchAccount } from "wagmi";
import { useTrackError } from "../../providers/TrackErrorProvider";
import { AccountActions, AccountState } from "../../stores/accountStore";
import { ConnectActions, ConnectState } from "../../stores/connectStore";
import { FlowActions, FlowState } from "../../stores/flowStore";
import { isUserRejectedRequestError, WalletError, WalletRejectedError } from "../lib/errors";
import { isMetaMaskConnector, useConnectors } from "./useConnectors";
import { useConnectSolanaWallet } from "./useConnectSolanaWallet";
export function useConnect({
  onConnected,
  isLinking
} = {}) {
  const trackError = useTrackError();
  const {
    connectAsync: connectEvmWallet
  } = useWagmiConnect();
  const connectSolanaWallet = useConnectSolanaWallet();
  const {
    switchAccountAsync
  } = useWagmiSwitchAccount();
  const {
    activeConnector
  } = useConnectors();
  const {
    isConnecting
  } = useSnapshot(AccountState);
  const {
    flow
  } = useSnapshot(FlowState);
  const flowType = flow?.type;
  const {
    onConnectedCallbacks
  } = useSnapshot(ConnectState);
  const {
    getChainIdentifier
  } = useChains();
  useEffect(function addOnConnectedCallbackToStore() {
    if (onConnected) {
      ConnectActions.addOnConnectedCallback(onConnected);
      return () => {
        ConnectActions.removeOnConnectedCallback(onConnected);
      };
    }
  }, [onConnected]);

  // Store the onConnected callbacks in a ref so that they are always up to date
  const onConnectedCallbacksRef = useRef(onConnectedCallbacks);
  useEffect(() => {
    onConnectedCallbacksRef.current = onConnectedCallbacks;
  }, [onConnectedCallbacks]);
  const connect = useCallback(async (connector, {
    address,
    switchOnConnect = true
  } = {}) => {
    if (!isLinking) {
      AccountActions.setShouldSyncAccountState(switchOnConnect);
    }
    let connectedAccount;
    let connectedAccounts;
    let connectedChainId;
    if (switchOnConnect) {
      AccountActions.setActiveChainArch(connector.chainArch);
    }
    if (connector.chainArch === "SVM") {
      if (connector.isConnected()) {
        connectedAccount = connector.baseConnector.publicKey?.toString() ?? "";
        connectedAccounts = [connectedAccount];
        // Solana is always chainId 1
        connectedChainId = 1;
      } else {
        AccountActions.setIsConnecting(true);
        try {
          const {
            accounts,
            chainId
          } = await connectSolanaWallet(connector.id);
          connectedAccount = accounts[0];
          connectedAccounts = accounts;
          connectedChainId = chainId;
        } catch (error) {
          trackError(error, {
            extra: {
              connector
            },
            tags: {
              source: "useConnect",
              connector: connector.name,
              chainArch: connector.chainArch
            }
          });
          throw error;
        } finally {
          AccountActions.setIsConnecting(false);
          if (!isLinking) {
            AccountActions.setShouldSyncAccountState(true);
          }
        }
      }
      AccountActions.setExternalAccount({
        address: connectedAccount,
        chainId: connectedChainId,
        connectorId: connector.id,
        addresses: connectedAccounts
      });
      onConnectedCallbacksRef.current.forEach(cb => cb(connectedAccount, getChainIdentifier(connectedChainId, "SVM"), connector));
      if (!(isLinking || switchOnConnect) && activeConnector) {
        connect(activeConnector);
      }
      return connectedAccount;
    }
    try {
      if (connector.isConnected()) {
        // Switch to connector if already connected for convenience
        const {
          accounts: accounts_0,
          chainId: chainId_0
        } = await switchAccountAsync({
          connector: connector.baseConnector
        });
        connectedAccount = accounts_0[0];
        connectedAccounts = accounts_0.slice();
        connectedChainId = chainId_0;
      } else {
        AccountActions.setIsConnecting(true);
        if (isMetaMaskConnector(connector.id) && !connector.isInstalled && flowType === "connect") {
          FlowActions.endFlow(); // Close modal to allow MM/WC modal to be accessible
        }
        const {
          accounts: accounts_1,
          chainId: chainId_1
        } = await connectEvmWallet({
          connector: connector.baseConnector
        });
        connectedAccount = accounts_1[0];
        connectedAccounts = accounts_1.slice();
        connectedChainId = chainId_1;
      }
      if (address && switchOnConnect) {
        AccountActions.switchAddress(address);
      }
    } catch (error_0) {
      trackError(error_0, {
        extra: {
          connector: {
            ...omit(connector, ["baseConnector"]),
            isConnected: connector.isConnected()
          }
        },
        tags: {
          source: "useConnect",
          connector: connector.name,
          chainArch: connector.chainArch
        }
      });
      let publicError;
      if (error_0 instanceof WalletError) {
        publicError = error_0;
      } else if (error_0 instanceof ViemBaseError && isUserRejectedRequestError(error_0)) {
        publicError = new WalletRejectedError(error_0);
      } else {
        publicError = new WalletError("Failed to connect to wallet", {
          cause: error_0
        });
      }
      throw publicError;
    } finally {
      AccountActions.setIsConnecting(false);
      if (!isLinking) {
        AccountActions.setShouldSyncAccountState(true);
      }
    }
    AccountActions.setExternalAccount({
      address: connectedAccount,
      chainId: connectedChainId,
      connectorId: connector.id,
      addresses: connectedAccounts
    });
    onConnectedCallbacksRef.current.forEach(cb_0 => cb_0(connectedAccount, getChainIdentifier(connectedChainId, "EVM"), connector));
    if (!(isLinking || switchOnConnect) && activeConnector) {
      connect(activeConnector);
    }
    return connectedAccount;
  }, [activeConnector, connectEvmWallet, flowType, connectSolanaWallet, switchAccountAsync, trackError, getChainIdentifier, isLinking]);
  return {
    ...useConnectors(),
    isConnecting,
    connect
  };
}